// Tenantra Inc. - Careers: job detail + application form
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { getCurrentUser, setTokens, setUser } from '@/services/auth';
import { track } from '@/services/analytics';
import { CareersLayout } from './CareersLayout';
import { COUNTRIES, REGIONS } from '@/data/regions';

const selectCls = 'h-10 w-full text-sm border border-slate-300 bg-white px-3 rounded-none focus:outline-none focus:ring-2 focus:ring-teal-600';

function OriginFields({ form, setForm, field }) {
  const states = REGIONS[form.country];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="country" className="text-xs text-slate-700">Country</Label>
        <select id="country" value={form.country} required onChange={(e) => setForm({ ...form, country: e.target.value, state_of_origin: '' })} className={selectCls} data-testid="apply-country">
          <option value="">Select country</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="state_of_origin" className="text-xs text-slate-700">State of origin</Label>
        {states ? (
          <select id="state_of_origin" value={form.state_of_origin} required onChange={(e) => setForm({ ...form, state_of_origin: e.target.value })} className={selectCls} data-testid="apply-state-of-origin">
            <option value="">Select state</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <Input id="state_of_origin" value={form.state_of_origin} required minLength={2} disabled={!form.country}
            placeholder={form.country ? 'Type your state or region' : 'Select a country first'}
            onChange={(e) => setForm({ ...form, state_of_origin: e.target.value })} className={field} data-testid="apply-state-of-origin-text" />
        )}
      </div>
    </div>
  );
}

export default function JobPage() {
  const slug = window.location.pathname.split('/')[2];
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);
  const user = getCurrentUser();
  const isCandidate = user?.role === 'candidate';

  useEffect(() => {
    api.get(`/careers/jobs/${slug}`).then(r => setJob(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const openForm = () => {
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <CareersLayout right={
      <a href={isCandidate ? '/careers/portal' : '/careers/login'} className="text-slate-600 hover:text-slate-900" data-testid="careers-nav-portal">
        {isCandidate ? 'My applications' : 'Candidate sign in'}
      </a>
    }>
      {notFound ? (
        <div className="max-w-3xl" data-testid="job-not-found">
          <h1 className="font-archivo text-2xl font-bold text-slate-900 mb-2">Position not found</h1>
          <a href="/careers" className="text-sm text-teal-700 hover:underline">Back to open positions</a>
        </div>
      ) : !job ? (
        <div className="max-w-3xl space-y-3">
          <div className="h-8 w-2/3 bg-slate-100 animate-pulse" />
          <div className="h-4 w-1/3 bg-slate-100 animate-pulse" />
          <div className="h-40 bg-slate-50 animate-pulse" />
        </div>
      ) : (
        <div className="max-w-3xl">
          <a href="/careers" className="text-xs text-slate-500 hover:text-slate-800" data-testid="job-back-link">← Open positions</a>
          <h1 className="font-archivo text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-4 mb-2" data-testid="job-title">{job.title}</h1>
          <p className="text-sm text-slate-500 mb-8" data-testid="job-meta">{job.location} · {job.employment_type}{job.compensation ? ` · ${job.compensation}` : ''}</p>

          {job.status === 'closed' && (
            <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 mb-8" data-testid="job-closed-notice">
              This position is no longer accepting applications.
            </div>
          )}

          <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-8">{job.summary}</p>

          {job.sections?.map((s, i) => (
            <section key={i} className="mb-8" data-testid={`job-section-${i}`}>
              <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3">{s.heading}</h2>
              {s.text && <p className="text-sm text-slate-700 leading-relaxed">{s.text}</p>}
              {s.items?.length > 0 && (
                <ul className="space-y-2">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                      <span className="w-1 h-1 rounded-full bg-slate-400 mt-2 flex-shrink-0"></span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {job.status === 'open' && !showForm && (
            <div className="border-t border-slate-200 pt-8">
              <Button onClick={openForm} className="h-10 px-6 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium" data-testid="job-apply-btn">
                Apply for this position
              </Button>
            </div>
          )}

          {showForm && (
            <div ref={formRef} className="border-t border-slate-200 pt-8">
              <ApplicationForm job={job} user={isCandidate ? user : null} />
            </div>
          )}
        </div>
      )}
    </CareersLayout>
  );
}

function ApplicationForm({ job, user }) {
  const [form, setForm] = useState({
    full_name: user?.full_name || '', email: user?.email || '', password: '', phone: '',
    country: '', state_of_origin: '',
    cover_letter: '', linkedin_url: '', portfolio_url: ''
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) { toast.error('Attach your resume'); return; }
    if (!user && form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    const fd = new FormData();
    fd.append('job_id', job.id);
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    fd.append('resume', resume);
    try {
      setSubmitting(true);
      const res = await api.post('/careers/apply', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTokens(res.data.access_token, res.data.refresh_token);
      setUser(res.data.user);
      track('job_application_submitted', { job_id: job.id });
      setDone(res.data);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="application-success">
        <h2 className="font-archivo text-xl font-bold text-slate-900 mb-2">Application received</h2>
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          Your application for {job.title} has been submitted.
          {done.created_account ? ' A candidate account was created with your email and password; use it to follow the status of your application.' : ''}
        </p>
        <a href="/careers/portal"><Button className="h-10 px-6 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium" data-testid="application-go-portal">View my applications</Button></a>
      </div>
    );
  }

  const field = 'h-10 text-sm border-slate-300 focus-visible:ring-teal-600 rounded-none';

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="application-form">
      <h2 className="font-archivo text-xl font-bold text-slate-900">Apply: {job.title}</h2>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="full_name" className="text-xs text-slate-700">Full name</Label>
          <Input id="full_name" value={form.full_name} onChange={set('full_name')} required className={field} data-testid="apply-full-name" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs text-slate-700">Phone</Label>
          <Input id="phone" value={form.phone} onChange={set('phone')} className={field} data-testid="apply-phone" />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs text-slate-700">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set('email')} required disabled={!!user} className={field} data-testid="apply-email" />
        </div>
        {!user && (
          <div>
            <Label htmlFor="password" className="text-xs text-slate-700">Create a password</Label>
            <Input id="password" type="password" value={form.password} onChange={set('password')} required minLength={8} className={field} data-testid="apply-password" />
            <p className="text-[11px] text-slate-500 mt-1">Used to sign in and follow your application. Minimum 8 characters.</p>
          </div>
        )}
      </div>

      <OriginFields form={form} setForm={setForm} field={field} />

      <div>
        <Label htmlFor="resume" className="text-xs text-slate-700">Resume (PDF or Word, max 8 MB)</Label>
        <input id="resume" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setResume(e.target.files?.[0] || null)} required
          className="block w-full text-sm text-slate-700 file:mr-3 file:h-10 file:px-4 file:border file:border-slate-300 file:bg-white file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50 border border-slate-300"
          data-testid="apply-resume" />
      </div>

      <div>
        <Label htmlFor="cover_letter" className="text-xs text-slate-700">Cover letter</Label>
        <Textarea id="cover_letter" value={form.cover_letter} onChange={set('cover_letter')} required minLength={20} rows={8}
          className="text-sm border-slate-300 focus-visible:ring-teal-600 rounded-none" data-testid="apply-cover-letter" />
        <p className="text-[11px] text-slate-500 mt-1">Include the short note requested in the posting, if any.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="linkedin_url" className="text-xs text-slate-700">LinkedIn URL (optional)</Label>
          <Input id="linkedin_url" type="url" value={form.linkedin_url} onChange={set('linkedin_url')} className={field} data-testid="apply-linkedin" />
        </div>
        <div>
          <Label htmlFor="portfolio_url" className="text-xs text-slate-700">Portfolio or work sample URL (optional)</Label>
          <Input id="portfolio_url" type="url" value={form.portfolio_url} onChange={set('portfolio_url')} className={field} data-testid="apply-portfolio" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 pt-2">
        <p className="text-[11px] text-slate-500">By submitting you agree to our <a href="/privacy-policy" className="underline">Privacy Policy</a>.</p>
        <Button type="submit" disabled={submitting} className="h-10 px-6 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium" data-testid="apply-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit application'}
        </Button>
      </div>
    </form>
  );
}
